<template>
  <BaseConsentGate
    :categories="consentCategories"
  >
    <div
      ref="container"
      class="embed-code"
    />
  </BaseConsentGate>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, ref, watch } from 'vue'

import BaseConsentGate from '@js/components/global/BaseConsentGate.vue'
import { type CookieConsentCategory } from '@js/composables/useCookieConsent'

defineOptions({
  name: 'AppEmbedCode',
})

const props = withDefaults(
  defineProps<{
    code: string
    consentCategories?: CookieConsentCategory[]
  }>(),
  {
    consentCategories: () => ['statistics'] as CookieConsentCategory[],
  },
)

const container = ref<HTMLElement | null>(null)

/**
 * Recreates a script element, as scripts set via innerHTML never execute.
 *
 * Widgets commonly do their work inside `window.onload` (BigMarker's does), which
 * has already fired by the time a consent gated embed is injected, so any handler
 * the script assigns has to be invoked here instead.
 */
const executeScript = (original: HTMLScriptElement, target: HTMLElement): void => {
  const script = document.createElement('script')

  Array.from(original.attributes).forEach(({ name, value }) => {
    script.setAttribute(name, value)
  })

  if (original.textContent) {
    script.textContent = original.textContent
  }

  // Appended scripts default to async, which loses the snippet's execution order
  script.async = false

  const onloadBeforeScript = window.onload

  const runAssignedOnload = (): void => {
    const assignedOnload = window.onload

    // The script assigned nothing, so there is no deferred work to run
    if (assignedOnload === onloadBeforeScript || typeof assignedOnload !== 'function') {
      return
    }

    // Still loading, so the browser will fire this handler itself
    if (document.readyState !== 'complete') {
      return
    }

    window.onload = onloadBeforeScript

    assignedOnload.call(window, new Event('load'))
  }

  script.addEventListener('load', runAssignedOnload)
  script.addEventListener('error', runAssignedOnload)

  target.appendChild(script)

  // Inline scripts run synchronously on append and fire no load event
  if (!script.src) {
    runAssignedOnload()
  }
}

const injectEmbed = (el: HTMLElement): void => {
  el.innerHTML = ''

  if (!props.code) {
    return
  }

  const template = document.createElement('template')
  template.innerHTML = props.code

  const scripts = Array.from(template.content.querySelectorAll('script'))
  scripts.forEach(script => script.remove())

  // Container elements must be in the document before the scripts run
  el.appendChild(template.content)

  scripts.forEach(original => executeScript(original, el))
}

// The slot only mounts once consent is granted, so the ref is populated then
watch(container, (el) => {
  if (el) {
    injectEmbed(el)
  }
})

onBeforeUnmount(() => {
  if (container.value) {
    container.value.innerHTML = ''
  }
})
</script>
